import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft } from 'lucide-react';

// ---------------------------------------------------------------------------
// Legal page scaffolding.
//
// These documents are DRAFTS prepared for review by counsel. Two switches
// control how they present, and both live here so there is exactly one place
// to change after sign-off:
//
//   UNDER_REVIEW  — renders the "draft, not yet in effect" banner on every
//                   legal page. Flip to false only when counsel has approved
//                   the text AND every <TBD> below has been filled in.
//   EFFECTIVE     — the effective date shown in each document's header.
//
// <TBD> marks a fact we cannot invent (entity details, governing law, notice
// addresses, retention periods). It renders in high-visibility amber so an
// unfilled blank cannot quietly ship to production.
// ---------------------------------------------------------------------------

export const UNDER_REVIEW = true;
export const EFFECTIVE = 'Draft — not yet in effect';

export const LEGAL_ENTITY = 'Adarga Health Technologies LLC';
export const PRODUCT = 'DoctorPaperwork';
export const CONTACT_EMAIL = 'privacy@adargamedical.com';
export const SECURITY_EMAIL = 'security@adargamedical.com';

export function TBD({ children }) {
  return (
    <span className="rounded bg-amber-200 px-1 font-mono text-[0.85em] text-amber-950 dark:bg-amber-900/60 dark:text-amber-100">
      [{children}]
    </span>
  );
}

export function H2({ id, children }) {
  return (
    <h2 id={id} className="scroll-mt-20 pt-8 text-lg font-semibold tracking-tight text-foreground">
      {children}
    </h2>
  );
}

export function H3({ children }) {
  return <h3 className="pt-4 font-semibold text-foreground">{children}</h3>;
}

export function P({ children }) {
  return <p className="pt-3 leading-relaxed text-muted-foreground">{children}</p>;
}

export function UL({ children }) {
  return <ul className="list-disc space-y-1.5 pt-3 pl-6 leading-relaxed text-muted-foreground">{children}</ul>;
}

/** Callout for the things a reader must not miss (role clarifications, limits). */
export function Note({ children }) {
  return (
    <div className="mt-4 rounded-xl border-l-4 border-primary bg-muted/50 p-4 text-sm leading-relaxed text-foreground">
      {children}
    </div>
  );
}

export default function LegalLayout({ title, subtitle, updated, children }) {
  useEffect(() => {
    document.title = `${title} · ${PRODUCT}`;
  }, [title]);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b bg-background/80 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-3xl items-center gap-3 px-4 lg:px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-logo">
            <div className="grid h-7 w-7 place-items-center rounded-lg bg-primary font-bold text-primary-foreground">M</div>
            <span className="font-semibold tracking-tight">{PRODUCT}</span>
          </Link>
          <div className="flex-1" />
          <Link
            to="/"
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            data-testid="legal-back-home"
          >
            <ArrowLeft className="h-4 w-4" /> Home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 pb-20 pt-10 lg:px-6">
        {UNDER_REVIEW && (
          <div
            className="mb-8 flex gap-3 rounded-xl border border-amber-400 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
            data-testid="legal-draft-banner"
            role="status"
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <span className="font-semibold">Draft under legal review — not yet in effect.</span> This document has
              not been approved by counsel and does not currently bind {LEGAL_ENTITY} or its customers. Amber
              highlights mark details still to be confirmed.
            </div>
          </div>
        )}

        <h1 className="text-3xl font-bold tracking-tight text-foreground">{title}</h1>
        {subtitle && <p className="pt-2 text-muted-foreground">{subtitle}</p>}
        <p className="pt-4 text-sm text-muted-foreground">
          <span className="font-medium text-foreground">Effective:</span> {EFFECTIVE}
          {updated && (
            <>
              {' · '}
              <span className="font-medium text-foreground">Last updated:</span> {updated}
            </>
          )}
        </p>

        <div className="pt-4 text-[0.95rem]">{children}</div>

        <nav className="mt-16 flex flex-wrap gap-x-4 gap-y-2 border-t pt-6 text-sm text-muted-foreground">
          <Link to="/terms" className="hover:text-foreground">Terms of Service</Link>
          <Link to="/privacy" className="hover:text-foreground">Privacy Policy</Link>
          <Link to="/hipaa" className="hover:text-foreground">HIPAA &amp; Health Information</Link>
          <Link to="/baa" className="hover:text-foreground">Business Associate Agreement</Link>
        </nav>
      </main>
    </div>
  );
}
